import { cn } from '@/lib/utils'

type LogoProps = {
  /** `full` = emblem + wordmark lockup; `emblem` = mark only. */
  variant?: 'full' | 'emblem'
  className?: string
}

/**
 * Masklabs brand logo, inlined so it's theme-aware: the emblem keeps the brand
 * blue (#00AEEF) while the wordmark uses `currentColor`, so it inherits the
 * surrounding text color (navy in light mode, near-white in dark).
 */
export default function Logo({ variant = 'full', className }: LogoProps) {
  if (variant === 'emblem') {
    return (
      <svg
        viewBox="0 0 552.54 404.14"
        className={cn('text-foreground', className)}
        fill="#00AEEF"
        role="img"
        aria-label="Masklabs"
      >
        <path d="M450.72,202.08l-116.71,202.07h113.65l94.7-164.03c13.59-23.53,13.59-52.53,0-76.06L447.65.01h-113.65l116.71,202.06Z" />
        <polygon points="218.51 0 207.79 3.06 182.01 30.38 170.08 67.32 158.92 93.88 161.69 98.42 169.5 99.74 198.75 39.62 219.24 10.47 218.51 0" />
        <path d="M101.82,202.09l116.69,202.04h-113.63L10.19,240.12c-13.59-23.53-13.59-52.52,0-76.06L104.88,0h113.63l-56.82,98.42-59.87,103.67Z" />
        <polygon points="343.6 216.69 218.47 0 161.69 98.39 286.82 315.08 343.6 216.69" />
      </svg>
    )
  }

  return (
    <svg
      viewBox="0 0 1046.64 148.37"
      className={cn('text-foreground', className)}
      role="img"
      aria-label="Masklabs"
    >
      <defs>
        <linearGradient
          id="masklabs-emblem-gradient"
          x1="57.56"
          y1="22.65"
          x2="127.95"
          y2="93.03"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#077fa3" />
          <stop offset=".27" stopColor="#0492c2" />
          <stop offset=".54" stopColor="#01a1da" />
          <stop offset=".79" stopColor="#00aae9" />
          <stop offset="1" stopColor="#00aeef" />
        </linearGradient>
      </defs>
      {/* Emblem, brand blue */}
      <g fill="#00aeef">
        <path d="M165.47,74.19l-42.85,74.18h41.72l34.77-60.22c4.99-8.64,4.99-19.29,0-27.93L164.35,0h-41.72l42.85,74.18Z" />
        <polygon points="80.22 0 76.29 1.12 66.82 11.15 62.44 24.72 58.34 34.46 59.36 36.13 62.23 36.62 72.97 14.54 80.49 3.84 80.22 0" />
        <path d="M37.38,74.19l42.84,74.17h-41.72L3.74,88.15c-4.99-8.64-4.99-19.28,0-27.92L38.5,0h41.72l-20.86,36.13-21.98,38.06Z" />
        <polygon
          fill="url(#masklabs-emblem-gradient)"
          points="126.15 79.55 80.21 0 59.36 36.12 105.3 115.68 126.15 79.55"
        />
      </g>
      {/* Wordmark, follows text color */}
      <g fill="currentColor">
        <path d="M363.78,9.47l-42.57,73.36-42.6-73.36h-27.76v129.43h22.34V41.91l41.44,72.04h12.76l41.65-72.04v96.99h22.31V9.47h-27.57Z" />
        <path d="M485.04,43.23v13.71c-7.87-9.95-19.5-15.95-33.57-15.95-27.02,0-47.07,21.58-47.07,50.07s20.05,50.1,47.07,50.1c14.08,0,25.71-6,33.57-15.95v13.68h21.03V43.23h-21.03ZM455.6,120.9c-17.08,0-29.63-12.95-29.63-29.84s12.55-29.81,29.63-29.81,29.44,12.95,29.44,29.81-12.55,29.84-29.44,29.84Z" />
        <path d="M542.06,68.75c0-5.81,5.81-9.76,14.63-9.76s15.21,4.5,15.21,11.08h21.39c0-17.26-14.63-29.07-36.42-29.07s-35.99,11.45-35.99,29.99c0,33.97,52.52,24.39,52.52,41.65,0,6-6.18,10.31-15.58,10.31-10.13,0-17.08-4.87-17.08-12.18h-21.36c0,18,15.18,30.39,38.26,30.39s37.15-12.21,37.15-30.79c0-34.12-52.73-24.94-52.73-41.62Z" />
        <path d="M662.45,93.05l-14.69,15.3,24.48,30.54h26.99l-36.79-45.85Z" />
        <polygon points="629.28 114.51 629.28 138.9 608.28 138.9 608.28 9.47 629.28 9.47 629.28 87.12 671.12 43.23 697.74 43.23 629.28 114.51" />
        <path d="M708.45,9.47v129.43h20.99V9.47h-20.99Z" />
        <path d="M824.35,43.23v13.71c-7.87-9.95-19.5-15.95-33.57-15.95-26.99,0-47.07,21.58-47.07,50.07s20.08,50.1,47.07,50.1c14.08,0,25.71-6,33.57-15.95v13.68h21.03V43.23h-21.03ZM794.91,120.9c-17.08,0-29.63-12.95-29.63-29.84s12.55-29.81,29.63-29.81,29.44,12.95,29.44,29.81-12.55,29.84-29.44,29.84Z" />
        <path d="M916.44,41c-14.05,0-25.71,6-33.76,15.95V9.47h-21.03v129.43h21.03v-13.68c8.05,9.95,19.71,15.95,33.76,15.95,26.84,0,47.1-21.58,47.1-50.1s-20.26-50.07-47.1-50.07ZM912.31,120.9c-16.86,0-29.63-12.95-29.63-29.84s12.76-29.81,29.63-29.81,29.47,12.95,29.47,29.81-12.58,29.84-29.47,29.84Z" />
        <path d="M993.9,68.75c0-5.81,5.81-9.76,14.63-9.76s15.21,4.5,15.21,11.08h21.39c0-17.26-14.63-29.07-36.39-29.07s-36.02,11.45-36.02,29.99c0,33.97,52.52,24.39,52.52,41.65,0,6-6.18,10.31-15.58,10.31-10.13,0-17.08-4.87-17.08-12.18h-21.36c0,18,15.18,30.39,38.26,30.39s37.15-12.21,37.15-30.79c0-34.12-52.73-24.94-52.73-41.62Z" />
      </g>
    </svg>
  )
}
